import { MONEY_PATTERN } from '@/utils'
import type { BuzzStatus } from './models'

/**
 * Every state the Hive Calls content can be in.
 *
 * There is one linked kind, and it carries each figure as its own nullable value: the
 * figures come from independent sources on nn (Points earned and Calls completed from
 * nn's ClickHouse read, Pending from Buzz), so one of them failing blanks that figure
 * only, never the whole card. `isBuzzUnavailable` describes the Buzz read, so it blanks
 * Pending only and drives the can't-load message; a `null` Points earned or Calls
 * completed does not set it.
 *
 * `isRefreshing` rides on the linked kind rather than owning a separate one: a
 * background refetch renders the same arm and the same markup, so "never a layout
 * shift while refreshing" holds by construction.
 */
export type BuzzCardState =
  | { kind: 'absent' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_linked' }
  | {
      kind: 'linked'
      pointsEarned: string | null
      paidCalls: number | null
      pending: string | null
      isBuzzUnavailable: boolean
      isRefreshing: boolean
    }

// `typeof` before the pattern in both money validators: `RegExp.test` stringifies its
// argument, so a numeric wire value would otherwise pass and break the
// strings-all-the-way invariant.
function validPointsEarned(value: unknown): string | null {
  return typeof value === 'string' && MONEY_PATTERN.test(value) ? value : null
}

// `Number.isSafeInteger` is not a type guard, hence the `typeof` first; no coercion,
// so a string `"7"` is malformed rather than a count.
function validPaidCalls(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null
}

/**
 * The unit check governs Pending only: `unit` labels `balance`, and the two new
 * fields are points by definition. `balance.available` is never read.
 */
function validPending(status: BuzzStatus): string | null {
  const { unit, balance } = status

  if (unit !== 'points' || balance === null || typeof balance !== 'object') {
    return null
  }

  return typeof balance.pending === 'string' && MONEY_PATTERN.test(balance.pending) ? balance.pending : null
}

/**
 * The single validator on the balance path, and the fail-fast gate the formatters
 * defer to: every figure is validated on its own and reaches the renderer either as
 * a well-formed value or as `null` ("—").
 *
 * Takes plain values, never a `UseQueryResult`, so the machine is independent of
 * react-query and testable as a table.
 *
 * `error` is derived only when the query has failed AND there is no data: react-query
 * keeps `data` on a failed background refetch, and that last good answer wins over the
 * error. A failed first fetch (a 500 from nn's own database, a network fault) would
 * otherwise sit in `loading` forever, since the query does not retry.
 *
 * An unknown `balanceState` never falls through: with `linked === false` it is
 * `not_linked`; otherwise it is linked with Pending unavailable.
 */
export function deriveBuzzCardState(input: {
  isEnabled: boolean
  isError: boolean
  status: BuzzStatus | undefined
  isFetching: boolean
}): BuzzCardState {
  if (input.isEnabled !== true) {
    return { kind: 'absent' }
  }

  if (input.status === undefined) {
    return input.isError ? { kind: 'error' } : { kind: 'loading' }
  }

  const status = input.status

  // The link state is decided before any figure is read, so a `not_linked` body that
  // carries a `pointsEarned` key still shows the onboarding.
  if (
    status.balanceState === 'not_linked' ||
    (status.balanceState !== 'ok' && status.balanceState !== 'unavailable' && status.linked === false)
  ) {
    return { kind: 'not_linked' }
  }

  const isOk = status.balanceState === 'ok'

  return {
    kind: 'linked',
    pointsEarned: validPointsEarned(status.pointsEarned),
    paidCalls: validPaidCalls(status.paidCalls),
    pending: isOk ? validPending(status) : null,
    isBuzzUnavailable: !isOk,
    isRefreshing: input.isFetching
  }
}
